'use client'

import React, { useEffect, useState } from 'react'
import PlanRadioGroup from './components/PlanRadioGroup'
import { cn } from '@paladise/ui/lib/utils'
import EnhancedExperienceTable from './components/EnhancedExperienceTable'
import CreatorToolTable from './components/CreatorToolTable'
import { ROUTE_PATH } from 'config/routerPath'
import { IProduct } from 'features/purchase/constants'
import { useRouter, useSearchParams } from 'next/navigation'
import { PlansTitle, PlansTypeId } from './constants'
import { postMessage } from 'features/webview/utils/postMessage'
import Plan from './components/Plan'
import PlanFooter from './components/PlanFooter'
import EUConsentModal from './components/EUConsentModal'
import { isEmbed, session } from 'store/server-context/static'
import { useSendTracker } from '@paladise/tracker/hooks/useSendTracker'
import { TRACKER_EVENTS } from '@paladise/tracker/constants/event'
import { IMethod, ISubscription } from 'features/payment/type'
import { currentPlanType } from './type'
import AddPaymentMethodModal from 'features/payment/components/modal/AddPaymentMethodModal'
import useToast from 'utils/hooks/useToast'
import { subscriptionPlan } from 'features/purchase/utils/subscription-plan'
import { getUserPaymentMethods } from 'api/payment'
import { clientFetch } from 'lib/fetch/client'
import DowngradeModal from './components/DowngradeModal'
import PaymentFailureDialog from 'features/payment/components/dialog/PaymentFailureDialog'
import { usePopupStore } from 'store/popup-store'
import {
  EXCEPTIONAL_PAGE_SCREEN_VIEW_TRACKER,
  POPUP_SCREEN_VIEW_TRACKER,
} from '@paladise/tracker/constants/screen'
import { SUBSCRIPTION_TYPE } from '@paladise/tracker/constants/parameter'
import { useTranslations } from 'next-intl'
import DefaultPaymentMethodDialog from 'features/payment/components/modal/DefaultPaymentMethodDialog'

interface Prop {
  className?: string
  hsaIosSubscription: boolean
  userSubscription: ISubscription
  plansMap: Record<string, IProduct>
}

function PlansPage({
  className,
  hsaIosSubscription,
  userSubscription,
  plansMap,
}: Prop) {
  const [isPaymentFailureOpen, setIsPaymentFailureOpen] = useState(false)
  const [EUConsent, setEUConsent] = useState(false)
  const [Downgrade, setDowngrade] = useState(false)
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(false)
  const [currentPlan, setCurrentPlan] = useState<currentPlanType>(
    PlansTypeId.Basic,
  )
  const [loading, setLoading] = useState(false)
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false)
  const { handleEventTracker } = useSendTracker()
  const { handleScreenTracker } = useSendTracker()

  const t = useTranslations()
  const { showSuccessToast } = useToast()
  const { push } = useRouter()
  const searchParams = useSearchParams()
  const isInEU = searchParams.get('isInEU') === 'true'
  const setAuthPopup = usePopupStore.use.setAuthPopup()

  const checkIsDowngrade = (plan: currentPlanType) => {
    const plansId = plansMap[plan]?.plan_id?.toString()
    const renew_plan_id = userSubscription?.renew_plan_id?.toString()
    if (renew_plan_id === PlansTypeId.Basic) {
      return false
    }
    if (renew_plan_id === PlansTypeId.Pro && plansId === PlansTypeId.Basic) {
      return true
    }
    if (
      renew_plan_id === PlansTypeId.ProPls &&
      (plansId === PlansTypeId.Basic || plansId === PlansTypeId.Pro)
    ) {
      return true
    }
    return false
  }

  const handleSubmit = async (plan: currentPlanType) => {
    if (loading) return
    handleEventTracker({
      eventName: TRACKER_EVENTS.btn_subscribe_click,
      parameters: { subscription_plan: currentPlan },
    })
    if (session.guest) {
      setAuthPopup(true)
      return
    }
    const paymentMethods = await getUserPaymentMethods(clientFetch)
    const defaultPaymentMethods = paymentMethods?.payment_methods.find(
      p => p.is_default,
    )
    if (!defaultPaymentMethods) {
      setIsAddPaymentOpen(true)
      return
    }
    setLoading(true)

    const isDowngrade = checkIsDowngrade(plan)
    if (isDowngrade) {
      setDowngrade(true)
      return
    }
    setDefaultPaymentMethod(true)
  }

  const handleSubscription = async (plan: currentPlanType) => {
    const plansId = plansMap[plan]?.id
    if (!plansId) throw new Error('Missing Plan id')
    try {
      const success = await subscriptionPlan(plansId)
      if (!success) throw new Error('Subscription Plan Failed')
      showSuccessToast({ title: t('subscribed') })
      handleScreenTracker({
        screenName: POPUP_SCREEN_VIEW_TRACKER.subscription_success_popup,
        parameters: {
          subscription_type: plansMap[plan]?.plan,
        },
      })

      if (isEmbed) {
        postMessage({
          type: 'purchaseCompleted',
          message: JSON.stringify({
            purchase_plan: plansMap[currentPlan]?.plan,
          }),
        })
      }
      push(ROUTE_PATH.SIDEBAR.FEED)
    } catch (error) {
      setIsPaymentFailureOpen(true)
    } finally {
      setLoading(false)
    }
  }

  const handleAddPaymentMethod = async (plan: currentPlanType) => {
    setIsAddPaymentOpen(false)
    setLoading(true)
    const isDowngrade = checkIsDowngrade(plan)
    if (isDowngrade) {
      setDowngrade(true)
      return
    }
    await handleSubscription(plan)
  }

  const plans = {
    [PlansTypeId['Basic']]: {
      key: PlansTypeId['Basic'],
      content: (
        <Plan
          loading={loading}
          amount={plansMap[PlansTypeId.Basic]?.amount || '-'}
          type={PlansTypeId['Basic']}
          value={PlansTypeId['Basic']}
          isCurrentUserPlan={
            userSubscription?.renew_plan_id?.toString() === PlansTypeId['Basic']
          }
          isCancel={userSubscription.cancel_at_period_end}
          hsaIosSubscription={hsaIosSubscription}
          onClick={() => {
            if (isInEU) {
              return setEUConsent(true)
            }
            handleSubmit(PlansTypeId['Basic'])
          }}
        />
      ),
      amount: plansMap[PlansTypeId.Basic]?.amount || '',
    },
    [PlansTypeId['Pro']]: {
      key: PlansTypeId['Pro'],
      content: (
        <Plan
          loading={loading}
          amount={plansMap[PlansTypeId.Pro]?.amount || '-'}
          type={PlansTypeId['Pro']}
          value={PlansTypeId['Pro']}
          isCurrentUserPlan={
            userSubscription?.renew_plan_id?.toString() === PlansTypeId['Pro']
          }
          isCancel={userSubscription.cancel_at_period_end}
          hsaIosSubscription={hsaIosSubscription}
          onClick={() => {
            if (isInEU) {
              return setEUConsent(true)
            }
            handleSubmit(PlansTypeId['Pro'])
          }}
        />
      ),
      amount: plansMap[PlansTypeId.Pro]?.amount || '',
    },
    [PlansTypeId['ProPls']]: {
      key: PlansTypeId['ProPls'],
      content: (
        <Plan
          loading={loading}
          amount={plansMap[PlansTypeId.ProPls]?.amount || '-'}
          type={PlansTypeId['ProPls']}
          value={PlansTypeId['ProPls']}
          isCurrentUserPlan={
            userSubscription?.renew_plan_id?.toString() ===
            PlansTypeId['ProPls']
          }
          isCancel={userSubscription.cancel_at_period_end}
          hsaIosSubscription={hsaIosSubscription}
          onClick={() => {
            if (isInEU) {
              return setEUConsent(true)
            }
            handleSubmit(PlansTypeId['ProPls'])
          }}
        />
      ),
      amount: plansMap[PlansTypeId.ProPls]?.amount || '',
    },
  }

  useEffect(() => {
    handleEventTracker({
      eventName: TRACKER_EVENTS.subscriptionplan_show,
      parameters: { subscription_plan: currentPlan },
    })

    handleScreenTracker({
      screenName: EXCEPTIONAL_PAGE_SCREEN_VIEW_TRACKER.subscriptions_page,
      parameters: {
        subscription_type: userSubscription.plan || SUBSCRIPTION_TYPE.general,
      },
    })
  }, [
    currentPlan,
    handleEventTracker,
    handleScreenTracker,
    userSubscription.plan,
  ])

  return (
    <div className={cn('text-label-l1 min768:pt-[100px] pt-[40px]', className)}>
      <div className="mx-auto w-full max-w-[840px] px-4">
        <h1 className="min768:text-bigTitle font-poppins mb-4 text-[24px] font-bold leading-[28.8px]">
          {t('plans_title')}
        </h1>
        <p className="text-body mb-[60px]">{t('plans_description')}</p>

        <PlanRadioGroup
          value={plans[currentPlan].key}
          onValueChange={v => {
            setCurrentPlan(plans[v].key)
          }}
          plans={Object.values(plans)}
        />

        <h2 className="text-label-l1 mb-12 mt-20 text-[28px] font-bold leading-[33.6px]">
          {t('plans_compare_tiers_features')}
        </h2>
        <EnhancedExperienceTable />
        <CreatorToolTable />
      </div>

      {currentPlan && (
        <PlanFooter
          title={t(PlansTitle[currentPlan])}
          amount={plans[currentPlan].amount}
          isCurrentUserPlan={
            userSubscription?.renew_plan_id?.toString() === currentPlan
          }
          loading={loading}
          hsaIosSubscription={hsaIosSubscription}
          isCancel={userSubscription.cancel_at_period_end}
          onClick={() => {
            if (isInEU) {
              return setEUConsent(true)
            }
            handleSubmit(currentPlan)
          }}
        />
      )}

      <AddPaymentMethodModal
        isOpen={isAddPaymentOpen}
        onOpenChange={setIsAddPaymentOpen}
        onSuccess={() => {
          handleAddPaymentMethod(currentPlan)
        }}
        onFailed={() => {
          setLoading(false)
        }}
      />

      <DefaultPaymentMethodDialog
        isOpen={defaultPaymentMethod}
        onOpenChange={setDefaultPaymentMethod}
        onClose={() => {
          setLoading(false)
        }}
        onSubmit={() => {
          handleSubscription(currentPlan)
        }}
      />
      <DowngradeModal
        isOpen={Downgrade}
        onOpenChange={setDowngrade}
        onCancel={() => {
          setLoading(false)
        }}
        onOk={async () => {
          await handleSubscription(currentPlan)
        }}
      />
      <EUConsentModal
        isOpen={EUConsent}
        onOpenChange={v => {
          if (!v && currentPlan) handleSubmit(currentPlan)
          setEUConsent(v)
        }}
      />

      <PaymentFailureDialog
        isOpen={isPaymentFailureOpen}
        onAction={() => {
          push(ROUTE_PATH.ACCOUNT.PAYMENT_METHOD)
        }}
        onClose={() => {
          setIsPaymentFailureOpen(false)
        }}
      />
    </div>
  )
}

export default PlansPage
